import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AuthProvider } from './hooks/useAuth';
import { BusinessProvider } from './contexts/BusinessContext';
import { AnimationProvider } from './contexts/AnimationContext';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BusinessRegistrationPage from './pages/BusinessRegistrationPage';
import RegistrationSuccessPage from './pages/RegistrationSuccessPage';
import AccountVerificationSentPage from './pages/AccountVerificationSentPage';
import DashboardPage from './pages/DashboardPage';
import BusinessDashboard from './pages/BusinessDashboard';
import OwnerBookingPage from './pages/OwnerBookingPage';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerAppointmentDetailsPage from './pages/CustomerAppointmentDetailsPage';
import CustomerQuoteRequestDetailsPage from './pages/CustomerQuoteRequestDetailsPage';
import CustomerInvoiceDetailsPage from './pages/CustomerInvoiceDetailsPage';
import InvoicePaymentPage from './pages/InvoicePaymentPage';
import InvoicePaymentCompletePage from './pages/InvoicePaymentCompletePage';
import CustomerAppointmentDepositPage from './pages/CustomerAppointmentDepositPage';
import CustomerAppointmentDepositCompletePage from './pages/CustomerAppointmentDepositCompletePage';
import GuestInvoiceDetailsPage from './pages/GuestInvoiceDetailsPage';
import GuestInvoicePaymentPage from './pages/GuestInvoicePaymentPage';
import GuestInvoicePaymentCompletePage from './pages/GuestInvoicePaymentCompletePage';

import AddServicePage from './pages/AddServicePage';
import EditServicePage from './pages/EditServicePage';
import DuplicateServicePage from './pages/DuplicateServicePage';
import EditPersonnelPage from './pages/EditPersonnelPage';
import InvitePersonnelPage from './pages/InvitePersonnelPage';
import BusinessWebsitePage from './pages/BusinessWebsitePage';
import LoginRequiredPage from './pages/LoginRequiredPage';
import InvitationPage from './pages/InvitationPage';
import BusinessBookingPage from './pages/BusinessBookingPage';
import BusinessAppointmentsPage from './pages/BusinessAppointmentsPage';
import CustomerSignupPage from './pages/CustomerSignupPage';
import AppointmentDetailsPage from './pages/AppointmentDetailsPage';
import BusinessEditAppointmentDetailsPage from './pages/BusinessEditAppointmentDetailsPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import BusinessChargeInvoicePage from './pages/BusinessChargeInvoicePage';
import BusinessChargeInvoiceCompletePage from './pages/BusinessChargeInvoiceCompletePage';
import BookingSelectionPage from './pages/BookingSelectionPage';
import InvoiceDetailsPage from './pages/InvoiceDetailsPage';
import QuoteRequestDetailsPage from './pages/QuoteRequestDetailsPage';
import CreateAppointmentFromQuotePage from './pages/CreateAppointmentFromQuotePage';
import RescheduleAppointmentPage from './pages/RescheduleAppointmentPage';
import BusinessRescheduleAppointmentPage from './pages/BusinessRescheduleAppointmentPage';
import BusinessAccountSettings from './pages/BusinessAccountSettings';
import CustomerProfilePage from './pages/CustomerProfilePage';
import BusinessProfilePage from './pages/BusinessProfilePage';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import ManageCategoriesPage from './pages/ManageCategoriesPage';
import HelpAndSupportPage from './pages/HelpAndSupportPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PlatformFeatures from './pages/PlatformFeatures';
import BusinessChallengePage from './pages/BusinessChallengePage';
import BusinessChallengeOfficialRulesPage from './pages/agreements/BusinessChallengeOfficialRulesPage';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import BlogCreatePage from './pages/BlogCreatePage';
import BlogAdminPage from './pages/BlogAdminPage';
import PlatformDashboardPage from './pages/PlatformDashboardPage';
import BusinessAccountAgreementPage from './pages/agreements/BusinessAccountAgreementPage';
import CustomerAccountAgreementPage from './pages/agreements/CustomerAccountAgreementPage';
import ProfessionalAccountAgreementPage from './pages/agreements/ProfessionalAccountAgreementPage';
import ProtectedRoute from './components/ProtectedRoute';
import TenantDashboardGuard from './components/TenantDashboardGuard';
import BusinessOwner2faGuard from './components/BusinessOwner2faGuard';
import PlatformAdminRoute from './components/PlatformAdminRoute';
import { PlatformAskAiHost, PlatformAskAiProvider } from './components/PlatformAskAiHost';
import LoadingSpinner from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import NotificationToastContainer from './components/NotificationToastContainer';
import { PwaInstallBanner } from './components/PwaInstallBanner';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { apiService } from './services/api';
import { useBusiness } from './contexts/BusinessContext';
import toast from 'react-hot-toast';
import { hasActiveBusinessRoles, isBusinessOwner, isPlatformSystemAdmin, isProfessional } from './utils/roleUtils';
import { usePeriodicRefreshOnNavigate } from './hooks/usePeriodicRefreshOnNavigate';
import { useMaintenanceCheck } from './hooks/useMaintenanceCheck';
import MaintenancePage from './pages/MaintenancePage';
import { sendPageView } from './analytics';
import AffiliatesAuthPage from './pages/AffiliatesAuthPage';
import AffiliateVerifyEmailPage from './pages/AffiliateVerifyEmailPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';

const isRunningAsPwa = (): boolean => {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
      // iOS Safari "Add to Home Screen"
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
};

const BusinessAppointmentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // `ProtectedRoute` should guarantee `user`, but guard defensively.
  if (!user) {
    return <Navigate to="/login?type=business" replace />;
  }

  const allowed = isBusinessOwner(user) || isProfessional(user);
  if (!allowed) {
    return (
      <Navigate
        to="/dashboard/customer/appointments"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  return <>{children}</>;
};

const TenantProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <TenantDashboardGuard>
      <BusinessOwner2faGuard>{children}</BusinessOwner2faGuard>
    </TenantDashboardGuard>
  </ProtectedRoute>
);

// Wrapper component for AddServicePage that handles the API call
const AddServicePageWrapper: React.FC<{ categories: { id: string; name: string }[] }> = ({ categories }) => {
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSaveService = async (serviceData: any, shouldReset?: boolean) => {
    if (!currentBusiness?.id) {
      toast.error('No business selected');
      return;
    }
    
    try {
      await apiService.createService({
        businessId: currentBusiness.id,
        name: serviceData.name,
        description: serviceData.description,
        durationMinutes: serviceData.duration,
        price: serviceData.price,
        quotable: serviceData.quotable,
        quoteRequestInstructions: serviceData.quoteRequestInstructions,
        categoryId: serviceData.category_id,
        icon: serviceData.icon,
        isAddOn: serviceData.isAddOn,
        serviceLocation: serviceData.serviceLocation,
      });
      toast.success('Service created successfully');
      
      // Only navigate away if shouldReset is false (checkbox unchecked)
      if (!shouldReset) {
        // Preserve filter params when navigating back
        const params = new URLSearchParams(location.search);
        params.set('section', 'services');
        navigate(`/dashboard/business?${params.toString()}`);
      }
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error('Failed to create service');
    }
  };

  return (
    <AddServicePage 
      onSave={handleSaveService}
      categories={categories}
      professionals={[
        { id: '1', name: 'Sarah Johnson' },
        { id: '2', name: 'Mike Chen' },
        { id: '3', name: 'Emily Davis' },
        { id: '4', name: 'David Wilson' }
      ]}
    />
  );
};

function isPrivateIndexPath(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (
    path === '/login' ||
    path === '/register' ||
    path === '/forgot-password' ||
    path === '/reset-password' ||
    path === '/blog-admin' ||
    path === '/blog/new' ||
    path.startsWith('/blog/edit/') ||
    path.startsWith('/dashboard') ||
    path.startsWith('/affiliates')
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const { currentBusiness } = useBusiness();
  const location = useLocation();
  const navigate = useNavigate();
  usePeriodicRefreshOnNavigate();
  useMaintenanceCheck();
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  useLayoutEffect(() => {
    const selector = 'meta[name="robots"][data-aacore-route="private"]';
    const existing = document.head.querySelector(selector);
    if (!isPrivateIndexPath(location.pathname)) {
      existing?.remove();
      return;
    }
    const meta = existing || document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, follow');
    meta.setAttribute('data-aacore-route', 'private');
    if (!existing) document.head.appendChild(meta);
  }, [location.pathname]);

  // Send GA4 page_view on every route change (SPA)
  useEffect(() => {
    const path = location.pathname + (location.search || '');
    sendPageView(path);
  }, [location.pathname, location.search]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const hasHandledPwaStartupRedirect = useRef(false);

  // When running as an installed PWA (WPA) we require auth even for otherwise-public routes.
  // This prevents a "bookmarked" deep-link from opening unauthenticated in the installed app.
  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (!isRunningAsPwa()) return;

    const pathname = location.pathname || '/';
    const allowedExactPaths = [
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password',
      '/verify-email',
      '/account-verification-sent',
      '/registration-success',
      '/business-registration',
      '/invitation',
      '/customer-signup',
      '/maintenance',
      '/privacy',
      '/features',
      '/business-challenge',
      '/business-challenge/official-rules',
    ];

    const allowedPrefixPaths = ['/agreements', '/affiliates'];

    const isAllowed =
      allowedExactPaths.includes(pathname) ||
      allowedPrefixPaths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    if (isAllowed) return;

    const redirectTarget = `${pathname}${location.search || ''}${location.hash || ''}`;
    navigate(`/login?type=customer&redirect=${encodeURIComponent(redirectTarget)}`, { replace: true });
  }, [loading, user, location.pathname, location.search, location.hash, navigate]);

  // PWA startup UX: if the installed app opens at the marketing home route (`/`) and the user
  // is already authenticated, take them straight to their dashboard.
  useEffect(() => {
    // Important: this should only run once per app launch (startup). Otherwise, clicking "Home"
    // inside the installed app will immediately bounce back to the dashboard.
    if (hasHandledPwaStartupRedirect.current) return;
    if (loading) return;
    if (!user) return;
    if (!isRunningAsPwa()) return;
    if ((location.pathname || '/') !== '/') return;

    hasHandledPwaStartupRedirect.current = true;

    if (isPlatformSystemAdmin(user)) {
      navigate('/dashboard/platform', { replace: true });
      return;
    }

    const userHasBusiness = hasActiveBusinessRoles(user);
    if (userHasBusiness) {
      const targetSection = isBusinessOwner(user) ? 'appointments' : 'my-appointments';
      navigate(`/dashboard/business?section=${targetSection}`, { replace: true });
      return;
    }

    navigate('/dashboard/customer', { replace: true });
  }, [loading, user, location.pathname, navigate]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        if (!currentBusiness?.id) {
          setCategories([]);
          return;
        }
        const categoriesData = await apiService.getServiceCategories(currentBusiness.id);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        // Fallback to default categories if API fails
        setCategories([
          { id: '1', name: 'Hair Services' },
          { id: '2', name: 'Nail Services' },
          { id: '3', name: 'Massage Therapy' },
          { id: '4', name: 'Facial Treatments' },
          { id: '5', name: 'Consultation' }
        ]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, [currentBusiness?.id, location.pathname]);

  if (loading || categoriesLoading) {
    return <LoadingSpinner />;
  }

  // Determine theme class from user or localStorage (to avoid flash on refresh)
  let storedUserTheme: string | undefined;
  try {
    const storedTheme = localStorage.getItem('preferedTheme');
    if (storedTheme) storedUserTheme = storedTheme;
    else {
      const saved = localStorage.getItem('user');
      if (saved) {
        const parsed = JSON.parse(saved);
        storedUserTheme = parsed?.preferedTheme;
      }
    }
  } catch {}
  const themeClass = (user?.preferedTheme || storedUserTheme) === 'neutral' ? 'theme-neutral'
    : (user?.preferedTheme || storedUserTheme) === 'pink' ? 'theme-pink'
    : (user?.preferedTheme || storedUserTheme) === 'baby-blue' ? 'theme-baby-blue'
    : 'theme-purple';

  return (
    <div className={`min-h-screen bg-gray-50 ${themeClass}`}>
      {/* Real-time notification toasts - only for authenticated users */}
      {user && <NotificationToastContainer />}
      {/* PWA install banner - gated by login + eligibility, hidden in installed PWA */}
      <PwaInstallBanner user={user} />
      <CookieConsentBanner />
      <PlatformAskAiProvider>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/login" element={
          user ? (
            (() => {
              const urlParams = new URLSearchParams(window.location.search);
              const redirectUrl = urlParams.get('redirect');
              const type = urlParams.get('type');
              if (isPlatformSystemAdmin(user)) {
                if (redirectUrl) {
                  return <Navigate to={redirectUrl} replace />;
                }
                return <Navigate to="/dashboard/platform" replace />;
              }
              const userHasBusiness = hasActiveBusinessRoles(user);
              if (type === 'business' && !userHasBusiness) {
                // Stay on login page to show error message
                return <LoginPage />;
              }
              // Prioritize business type over redirectUrl
              if (type === 'business' && userHasBusiness) {
                return <Navigate to="/dashboard/business?section=appointments" />;
              }
              // Handle professional type - redirect to business dashboard if they have business roles
              if (type === 'professional') {
                if (userHasBusiness) {
                  const targetSection = isBusinessOwner(user) ? 'appointments' : 'my-appointments';
                  return <Navigate to={`/dashboard/business?section=${targetSection}`} />;
                } else {
                  // If no business roles, treat as customer session
                  if (redirectUrl) {
                    return <Navigate to={redirectUrl} />;
                  }
                  return <Navigate to="/dashboard/customer" />;
                }
              }
              if (redirectUrl) {
                return <Navigate to={redirectUrl} />;
              }
              return <Navigate to="/dashboard/customer" />;
            })()
          ) : (
            <LoginPage />
          )
        } />
        <Route path="/register" element={user ? <Navigate to={isPlatformSystemAdmin(user) ? '/dashboard/platform' : '/dashboard/customer'} /> : <RegisterPage />} />
        <Route path="/forgot-password" element={user ? <Navigate to={isPlatformSystemAdmin(user) ? '/dashboard/platform' : '/dashboard/customer'} /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={user ? <Navigate to={isPlatformSystemAdmin(user) ? '/dashboard/platform' : '/dashboard/customer'} /> : <ResetPasswordPage />} />
        <Route path="/business-registration" element={<BusinessRegistrationPage />} />
        <Route path="/registration-success" element={<RegistrationSuccessPage />} />
        <Route path="/account-verification-sent" element={<AccountVerificationSentPage />} />
        <Route path="/invitation" element={<InvitationPage />} />
        <Route path="/customer-signup" element={<CustomerSignupPage />} />
        <Route path="/agreements/business" element={<BusinessAccountAgreementPage />} />
        <Route path="/agreements/customer" element={<CustomerAccountAgreementPage />} />
        <Route path="/agreements/professional" element={<ProfessionalAccountAgreementPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/features" element={<PlatformFeatures />} />
        <Route path="/business-challenge" element={<BusinessChallengePage />} />
        <Route path="/business-challenge/official-rules" element={<BusinessChallengeOfficialRulesPage />} />
        <Route path="/blog/new" element={<BlogCreatePage />} />
        <Route path="/blog/edit/:slug" element={<BlogCreatePage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route
          path="/blog-admin"
          element={
            <ProtectedRoute>
              <PlatformAdminRoute>
                <BlogAdminPage />
              </PlatformAdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Dashboard Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              {user ? (
                isPlatformSystemAdmin(user) ? (
                  <Navigate to="/dashboard/platform" replace />
                ) : (
                  <Navigate to="/dashboard/customer" replace />
                )
              ) : (
                <DashboardPage />
              )}
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/platform" 
          element={
            <ProtectedRoute>
              <PlatformAdminRoute>
                <PlatformDashboardPage />
              </PlatformAdminRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/platform/profile" 
          element={
            <ProtectedRoute>
              <PlatformAdminRoute>
                <CustomerProfilePage />
              </PlatformAdminRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/business" 
          element={
            <TenantProtectedRoute>
              <BusinessDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/business/reschedule/:appointmentId" 
          element={
            <TenantProtectedRoute>
              <BusinessRescheduleAppointmentPage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/business/create-appointment" 
          element={
            <TenantProtectedRoute>
              <OwnerBookingPage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/business/customers/:customerId" 
          element={
            <TenantProtectedRoute>
              <CustomerDetailsPage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />

        <Route 
          path="/dashboard/customer/appointments" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/quote-requests" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/quote-requests/:quoteId" 
          element={
            <TenantProtectedRoute>
              <CustomerQuoteRequestDetailsPage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/invoices" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/invoices/:invoiceId" 
          element={
            <TenantProtectedRoute>
              <CustomerInvoiceDetailsPage />
            </TenantProtectedRoute>
          } 
        />
        <Route
          path="/dashboard/customer/invoices/:invoiceId/pay"
          element={
            <TenantProtectedRoute>
              <InvoicePaymentPage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/customer/invoices/:invoiceId/pay/complete"
          element={
            <TenantProtectedRoute>
              <InvoicePaymentCompletePage />
            </TenantProtectedRoute>
          }
        />

        <Route path="/guest/invoices/:invoiceId" element={<GuestInvoiceDetailsPage />} />
        <Route path="/guest/invoices/:invoiceId/pay" element={<GuestInvoicePaymentPage />} />
        <Route path="/guest/invoices/:invoiceId/pay/complete" element={<GuestInvoicePaymentCompletePage />} />
        <Route 
          path="/dashboard/customer/gift-cards" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/settings" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/profile" 
          element={
            <TenantProtectedRoute>
              <CustomerProfilePage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/business/profile" 
          element={
            <TenantProtectedRoute>
              <BusinessProfilePage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/notifications" 
          element={
            <TenantProtectedRoute>
              <CustomerDashboard />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/customer/reschedule/:appointmentId" 
          element={
            <TenantProtectedRoute>
              <RescheduleAppointmentPage />
            </TenantProtectedRoute>
          } 
        />



        
        {/* Service Management Routes */}
        <Route 
          path="/services/add" 
          element={
            <TenantProtectedRoute>
              <AddServicePageWrapper categories={categories} />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/services/edit/:serviceId" 
          element={
            <TenantProtectedRoute>
              <EditServicePage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/services/duplicate/:serviceId" 
          element={
            <TenantProtectedRoute>
              <DuplicateServicePage />
            </TenantProtectedRoute>
          } 
        />
        
        {/* Personnel Management Routes */}
        <Route 
          path="/personnel/edit/:personnelId" 
          element={
            <TenantProtectedRoute>
              <EditPersonnelPage />
            </TenantProtectedRoute>
          } 
        />
        <Route 
          path="/personnel/invite/:businessId" 
          element={
            <TenantProtectedRoute>
              <InvitePersonnelPage />
            </TenantProtectedRoute>
          } 
        />
        
        {/* Appointment Management Routes */}
        <Route 
          path="/appointments/:appointmentId" 
          element={
            <TenantProtectedRoute>
              <BusinessAppointmentRoute>
                <AppointmentDetailsPage />
              </BusinessAppointmentRoute>
            </TenantProtectedRoute>
          } 
        />

        <Route
          path="/dashboard/business/appointments/:appointmentId/edit"
          element={
            <TenantProtectedRoute>
              <BusinessEditAppointmentDetailsPage />
            </TenantProtectedRoute>
          }
        />

        <Route
          path="/dashboard/business/appointments/:appointmentId/create-invoice"
          element={
            <TenantProtectedRoute>
              <CreateInvoicePage />
            </TenantProtectedRoute>
          }
        />

        <Route
          path="/dashboard/business/invoices/create"
          element={
            <TenantProtectedRoute>
              <CreateInvoicePage />
            </TenantProtectedRoute>
          }
        />

        <Route
          path="/dashboard/business/invoices/booking-selection"
          element={
            <TenantProtectedRoute>
              <BookingSelectionPage />
            </TenantProtectedRoute>
          }
        />

        <Route
          path="/dashboard/business/invoices/:invoiceId/charge/complete"
          element={
            <TenantProtectedRoute>
              <BusinessChargeInvoiceCompletePage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/business/invoices/:invoiceId/charge"
          element={
            <TenantProtectedRoute>
              <BusinessChargeInvoicePage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/business/invoices/:invoiceId"
          element={
            <TenantProtectedRoute>
              <InvoiceDetailsPage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/business/quote-requests/:quoteId/create-appointment"
          element={
            <TenantProtectedRoute>
              <CreateAppointmentFromQuotePage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/business/quote-requests/:quoteId"
          element={
            <TenantProtectedRoute>
              <QuoteRequestDetailsPage />
            </TenantProtectedRoute>
          }
        />
        
        {/* Customer Appointment Details Route */}
        <Route 
          path="/dashboard/customer/appointments/:appointmentId/deposit/complete"
          element={
            <TenantProtectedRoute>
              <CustomerAppointmentDepositCompletePage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/customer/appointments/:appointmentId/deposit"
          element={
            <TenantProtectedRoute>
              <CustomerAppointmentDepositPage />
            </TenantProtectedRoute>
          }
        />
        <Route
          path="/dashboard/customer/appointments/:appointmentId"
          element={
            <TenantProtectedRoute>
              <CustomerAppointmentDetailsPage />
            </TenantProtectedRoute>
          }
        />
        
        {/* Email Verification Route */}
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        
        {/* Login Required Route */}
        <Route path="/:businessShortName/login-required" element={<LoginRequiredPage />} />
        
        {/* Business Booking Route */}
        <Route 
          path="/:businessShortName/booking" 
          element={
            <ProtectedRoute>
              <BusinessBookingPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Business Appointments Route */}
        <Route 
          path="/:businessShortName/appointments" 
          element={
            <ProtectedRoute>
              <BusinessAppointmentsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Business Account Settings Route */}
        <Route 
          path="/account-settings" 
          element={
            <ProtectedRoute>
              <BusinessAccountSettings />
            </ProtectedRoute>
          } 
        />
        
        {/* Categories Management Route */}
        <Route 
          path="/categories" 
          element={
            <ProtectedRoute>
              <ManageCategoriesPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Help & Support Route */}
        <Route 
          path="/help-support" 
          element={
            <ProtectedRoute>
              <HelpAndSupportPage />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/affiliates" element={<AffiliatesAuthPage />} />
        <Route path="/affiliates/verify-email" element={<AffiliateVerifyEmailPage />} />
        <Route path="/affiliates/dashboard" element={<AffiliateDashboardPage />} />

        {/* Public Business Website Route - must be last to avoid conflicts */}
        <Route path="/:businessShortName" element={<BusinessWebsitePage />} />
      </Routes>
      <PlatformAskAiHost />
      </PlatformAskAiProvider>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BusinessProvider>
          <AnimationProvider>
            <AppContent />
          </AnimationProvider>
        </BusinessProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App; 